import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import AiAnalysisPage from "./pages/AiAnalysisPage";
import BillingPage from "./pages/BillingPage";
import ClientBillingPage from "./pages/ClientBillingPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import AddClientPage from "./pages/AddClientPage";
import EditClientPage from "./pages/EditClientPage";
import ArchivedClientsPage from "./pages/ArchivedClientsPage";
import ClientsPage from "./pages/ClientsPage";
import DashboardPage from "./pages/DashboardPage";
import DisputeLetterPage from "./pages/DisputeLetterPage";
import DisputesPage from "./pages/DisputesPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import StaffPage from "./pages/StaffPage";
import CompliancePage from "./pages/CompliancePage";
import ContractTemplatesPage from "./pages/ContractTemplatesPage";
import LandingPage from "./pages/marketing/LandingPage";
import PricingPage from "./pages/marketing/PricingPage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import AffiliatesDashboardPage from "./pages/AffiliatePage";
import AutomationsPage from "./pages/AutomationsPage";
import { default as ClientImportPage } from "./pages/ImportPage";
import PortalLoginPage from "./pages/portal/PortalLoginPage";
import PortalDashboardPage from "./pages/portal/PortalDashboardPage";
import PortalDisputesPage from "./pages/portal/PortalDisputesPage";
import PortalDocumentsPage from "./pages/portal/PortalDocumentsPage";
import { PortalProtectedRoute } from "./pages/portal/PortalLayout";
import SignContractPage from "./pages/public/SignContractPage";
import { useAuth } from "./hooks/useAuth";
import DocumentTitle from "./components/DocumentTitle";
import NotFoundPage from "./pages/NotFoundPage";
import ScoreTrackerPage from "./pages/ScoreTrackerPage";
import TaskManagerPage from "./pages/TaskManagerPage";
import SubscriptionGuard from "./components/SubscriptionGuard";
import DiySubscriptionGuard from "./components/DiySubscriptionGuard";
import DiyLandingPage from "./pages/diy/DiyLandingPage";
import DiyRegisterPage from "./pages/diy/DiyRegisterPage";
import DiyDashboardPage from "./pages/diy/DiyDashboardPage";
import DiyMyReportPage from "./pages/diy/DiyMyReportPage";
import DiyMyDisputesPage from "./pages/diy/DiyMyDisputesPage";
import DiyMyLettersPage from "./pages/diy/DiyMyLettersPage";
import DiyDocumentsPage from "./pages/diy/DiyDocumentsPage";
import DiyAiCoachPage from "./pages/diy/DiyAiCoachPage";
import DiySettingsPage from "./pages/diy/DiySettingsPage";
import { getTenantTypeFromToken } from "./utils/jwtUtils";

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const OperatorOnlyRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (getTenantTypeFromToken(token) === "diy") {
    return <Navigate to="/diy/dashboard" replace />;
  }
  return children;
};

const DiyProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (!isAuthenticated) {
    return <Navigate to="/login?returnUrl=/diy/dashboard" replace />;
  }
  if (getTenantTypeFromToken(token) !== "diy") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const ProtectedAppRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <OperatorOnlyRoute>
        <SubscriptionGuard>{children}</SubscriptionGuard>
      </OperatorOnlyRoute>
    </ProtectedRoute>
  );
};

const DiyAppShell: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <DiyProtectedRoute>
    <DiySubscriptionGuard>{children}</DiySubscriptionGuard>
  </DiyProtectedRoute>
);

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <>
      <DocumentTitle />
      <div key={location.pathname} className="iw-page-transition min-h-screen">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/diy" element={<DiyLandingPage />} />
          <Route path="/diy/register" element={<DiyRegisterPage />} />
          <Route path="/diy/dashboard" element={<DiyAppShell><DiyDashboardPage /></DiyAppShell>} />
          <Route path="/diy/my-report" element={<DiyAppShell><DiyMyReportPage /></DiyAppShell>} />
          <Route path="/diy/my-disputes" element={<DiyAppShell><DiyMyDisputesPage /></DiyAppShell>} />
          <Route path="/diy/my-letters" element={<DiyAppShell><DiyMyLettersPage /></DiyAppShell>} />
          <Route path="/diy/documents" element={<DiyAppShell><DiyDocumentsPage /></DiyAppShell>} />
          <Route path="/diy/ai-coach" element={<DiyAppShell><DiyAiCoachPage /></DiyAppShell>} />
          <Route path="/diy/settings" element={<DiyAppShell><DiySettingsPage /></DiyAppShell>} />

          <Route path="/sign/:token" element={<SignContractPage />} />

          <Route path="/portal" element={<Outlet />}>
            <Route path="login" element={<PortalLoginPage />} />
            <Route element={<PortalProtectedRoute />}>
              <Route path="dashboard" element={<PortalDashboardPage />} />
              <Route path="disputes" element={<PortalDisputesPage />} />
              <Route path="documents" element={<PortalDocumentsPage />} />
            </Route>
          </Route>
          <Route
            path="/dashboard"
            element={
              <ProtectedAppRoute>
                <DashboardPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedAppRoute>
                <ClientsPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/clients/add"
            element={
              <ProtectedAppRoute>
                <AddClientPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/clients/archived"
            element={
              <ProtectedAppRoute>
                <ArchivedClientsPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedAppRoute>
                <ClientDetailPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/clients/:id/edit"
            element={
              <ProtectedAppRoute>
                <EditClientPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/disputes"
            element={
              <ProtectedAppRoute>
                <DisputesPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/dispute-letters"
            element={
              <ProtectedAppRoute>
                <DisputeLetterPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/ai-analysis"
            element={
              <ProtectedAppRoute>
                <AiAnalysisPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedAppRoute>
                <BillingPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/client-billing"
            element={
              <ProtectedAppRoute>
                <ClientBillingPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <ProtectedAppRoute>
                <CompliancePage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/contracts/templates"
            element={
              <ProtectedAppRoute>
                <ContractTemplatesPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedAppRoute>
                <ReportsPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/score-tracker"
            element={
              <ProtectedAppRoute>
                <ScoreTrackerPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedAppRoute>
                <TaskManagerPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedAppRoute>
                <SettingsPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <ProtectedAppRoute>
                <StaffPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/affiliates"
            element={
              <ProtectedAppRoute>
                <AffiliatesDashboardPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/automations"
            element={
              <ProtectedAppRoute>
                <AutomationsPage />
              </ProtectedAppRoute>
            }
          />
          <Route
            path="/import"
            element={
              <ProtectedAppRoute>
                <ClientImportPage />
              </ProtectedAppRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return <AnimatedRoutes />;
}

export default App;
